// Lazy import Sentry to avoid module side effects
// Tests use module aliasing to replace @sentry/react with a mock
let Sentry: typeof import("@sentry/react") | null = null;

async function getSentry() {
  if (!Sentry) {
    Sentry = await import("@sentry/react");
  }
  return Sentry;
}

export async function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const stage = import.meta.env.VITE_STAGE ?? "dev";
  const { id: versionId } =
    (typeof import.meta.env.VITE_CF_VERSION_METADATA === "string"
      ? JSON.parse(import.meta.env.VITE_CF_VERSION_METADATA)
      : import.meta.env.VITE_CF_VERSION_METADATA) ?? {};

  if (!dsn) {
    console.log(
      `[Sentry] DSN not configured, skipping initialization for stage ${stage} and version ${versionId}`
    );
    return;
  }
  console.log(`[Sentry] Initializing with version ${versionId}`);

  const escapedStage = stage.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const isDev = stage === "dev" || stage === "development";

  const sentry = await getSentry();
  if (!sentry) return;

  const isProd = stage === "prod";

  sentry.init({
    dsn,
    enabled: !isDev, // Disable in development
    environment: `web-${stage}`,
    release: versionId,
    tracesSampleRate: isProd ? 0.1 : 1.0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/simpleislands\.com/,
      /^https:\/\/api.simpleislands\.com/,
      ...(stage.startsWith("pr-")
        ? [
            new RegExp(`^https://${escapedStage}\\.preview\\.simpleislands\\.com`),
            new RegExp(`^https://api\\.${escapedStage}\\.preview\\.simpleislands\\.com`),
          ]
        : []),
    ],
    integrations: [
      sentry.browserTracingIntegration(),
      sentry.replayIntegration(),
      // Limit console capture to errors/warnings in production to reduce noise
      sentry.consoleLoggingIntegration({
        levels: isProd ? ["log", "info", "warn", "error"] : ["log", "info", "warn", "error"],
      }),
    ],
    replaysSessionSampleRate: isProd ? 0.05 : 0.1, // Lower in prod to reduce cost
    replaysOnErrorSampleRate: 1.0,
    // Disable automatic PII collection - we explicitly set user context via setSentryUser()
    sendDefaultPii: false,
    enableLogs: true,
  });
}

export function setSentryUser(user: { id: string; email: string; name?: string | null }) {
  if (!Sentry) return;
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name ?? undefined,
    name: user.name ?? undefined,
  });
}

export function clearSentryUser() {
  if (!Sentry) return;
  Sentry.setUser(null);
}

export async function sendToSentry(error: unknown, data: { url: string; method: string }) {
  const sentry = await getSentry();
  if (!sentry) return;
  sentry.captureException(error, {
    contexts: {
      request: { url: data.url, method: data.method },
    },
  });
}
